// Pre-sales on production runs (ERP-PLAN §5.14), shared by the screens, the server, the MCP, the bookkeeper pack and
// tests, so every number agrees: labels and field schemas, a run's reserved / shipped / remaining / held / retail
// left per item, what planning must not count as for sale (planReservations()), what's set aside at the CM for
// backers (cmClaims()), and a pre-sale's result (income − its units at landed cost − shipping to backers).
//
// Counts and totals only: HHL Ops never holds a backer's name, email or address (they stay in the backers app).
//
// How the goods move (GC Ultimate 2's Kickstarter): the CM packs one box per backer from the run's output. US boxes go
// in master cartons to HHL's Philadelphia warehouse and ship from there as Shopify orders that bypass inventory;
// international boxes go to a DDP forwarder. So a pre-sale that ships from the CM ("cm") never brings its units to
// HHL: the run's make PO keeps its quantity and its bill, and each make PO line's `qty_direct` (kept by the database,
// presale_allocate() in migration 029) is what goes straight to backers; receiving, Shopify's incoming transfer and
// planning expect only the rest at HHL. A pre-sale that ships from HHL ("hhl", pre-orders) and a held allocation
// (gifts, influencers) arrive at HHL as usual and are kept out of what's for sale until they ship or are released.
import { z } from "zod";
import type { ConsignClaim } from "./consignment.ts";
import { basisOf, type CostIndex, costCents, knownUnitCost, landedCostOf, plannedPrice, runBoughtPrice, runResult } from "./costs.ts";
import type {
  Attachment,
  Item,
  Location,
  Po,
  PoLine,
  Presale,
  PresaleIncome,
  PresaleLine,
  PresaleShipment,
  PresaleShipmentLine,
  ProductionCost,
  ProductionRun,
  ProductionRunHold,
  ProductionRunLine,
} from "./db.gen.ts";
import { groupBy } from "./group.ts";
import type { PlanReservation } from "./planning.ts";
import { mulRound } from "./po.ts";

// Labels -----------------------------------------------------------------------------------------------------------

export const PRESALE_CHANNELS = ["kickstarter", "pledgebox", "preorder", "wholesale", "other"] as const;
export type PresaleChannel = (typeof PRESALE_CHANNELS)[number];
export const CHANNEL_LABELS: Record<PresaleChannel, string> = {
  kickstarter: "Kickstarter",
  pledgebox: "PledgeBox",
  preorder: "Pre-order",
  wholesale: "Wholesale",
  other: "Other",
};

export const PRESALE_STATUSES = ["open", "shipping", "done", "cancelled"] as const;
export type PresaleStatus = (typeof PRESALE_STATUSES)[number];
export const PRESALE_STATUS_LABELS: Record<PresaleStatus, string> = { open: "Open", shipping: "Shipping", done: "Done", cancelled: "Cancelled" };
export const PRESALE_STATUS_HINTS: Record<PresaleStatus, string> = {
  open: "Still taking orders or finishing surveys: the counts can change",
  shipping: "Boxes are going out to backers",
  done: "Everything has shipped: nothing is reserved any more",
  cancelled: "Called off: it reserves nothing",
};
export const PRESALE_STATUS_TONE = { open: "accent", shipping: "warn", done: "ok", cancelled: "neutral" } as const;
/** A pre-sale that still reserves units. */
export const presaleLive = (s: string) => s === "open" || s === "shipping";

export const SHIPS_FROM = ["cm", "hhl"] as const;
export const SHIPS_FROM_LABELS = {
  cm: "From the CM: packed there, never comes to HHL",
  hhl: "From HHL: arrives with the rest, kept out of what's for sale",
} as const;

export const INCOME_SOURCES = ["kickstarter", "pledgebox", "shopify", "other"] as const;
export type IncomeSource = (typeof INCOME_SOURCES)[number];
export const INCOME_SOURCE_LABELS: Record<IncomeSource, string> = { kickstarter: "Kickstarter", pledgebox: "PledgeBox", shopify: "Shopify", other: "Other" };
export const INCOME_KINDS = ["payout", "fees", "refund", "shipping", "other"] as const;
export type IncomeKind = (typeof INCOME_KINDS)[number];
export const INCOME_KIND_LABELS: Record<IncomeKind, string> = {
  payout: "Payout",
  fees: "Fees",
  refund: "Refund",
  shipping: "Shipping collected",
  other: "Other",
};
export const INCOME_KIND_HINTS: Record<IncomeKind, string> = {
  payout: "Money paid out to HHL, as paid out: fees already taken off",
  fees: "Fees taken separately (not already out of a payout)",
  refund: "Money given back to backers",
  shipping: "Shipping charged to backers separately",
  other: "Anything else: a positive amount came in, a negative one went out",
};
/** Kinds that are money out (stored positive). */
export const INCOME_OUT: ReadonlySet<string> = new Set(["fees", "refund"]);

export const REGIONS = ["us", "intl", "all"] as const;
export type Region = (typeof REGIONS)[number];
export const REGION_LABELS: Record<Region, string> = { us: "US", intl: "International", all: "US and international" };

export const SHIPPING_KINDS = ["postage", "ddp", "packing", "freight", "other"] as const;
export type ShippingKind = (typeof SHIPPING_KINDS)[number];
export const SHIPPING_KIND_LABELS: Record<ShippingKind, string> = {
  postage: "Postage",
  ddp: "DDP",
  packing: "Packing fee",
  freight: "Freight to Philadelphia",
  other: "Other shipping",
};

// No customer data ---------------------------------------------------------------------------------------------------
// HHL Ops keeps counts and totals only: never a backer's email, @handle or PledgeBox order number. One guard for every
// way in: the pre-sale tables' text fields (mutations and the import), shipping-to-backers costs, the import's file.

export const NO_CONTACT = "HHL Ops keeps no backer contact details: leave out emails and @handles";
export const NO_ORDER_NUMBERS = "HHL Ops keeps no PledgeBox order numbers: leave them out";
/** A PledgeBox order number, as the backers app's own export refuses it (four capitals and 9–10 digits). */
export const ORDER_NUMBER = /\b[A-Z]{4}\d{9,10}\b/;

/** Why a text can't come in (an "@", or with `orderNumbers` a PledgeBox order number), or null when it can. */
export function customerData(text: string, opts: { orderNumbers?: boolean } = {}): string | null {
  if (text.includes("@")) return NO_CONTACT;
  if (opts.orderNumbers && ORDER_NUMBER.test(text)) return NO_ORDER_NUMBERS;
  return null;
}

/**
 * Every string in a parsed JSON value, keys too, with where it is ("units.GCU1300.name"; a key's `parent` is where it
 * sits). Keys come before what's under them, so a path is only ever made of keys already looked at.
 */
export function* stringsIn(value: unknown, path = ""): Generator<{ path: string; text: string; key?: boolean; parent?: string }> {
  if (typeof value === "string") yield { path, text: value };
  else if (Array.isArray(value)) for (const [i, v] of value.entries()) yield* stringsIn(v, `${path}[${i}]`);
  else if (value && typeof value === "object")
    for (const [k, v] of Object.entries(value)) {
      const at = path ? `${path}.${k}` : k;
      yield { path: at, text: k, key: true, parent: path };
      yield* stringsIn(v, at);
    }
}

/** Uploads on income entries and shipping-to-backers costs: the payout statement or the summary invoice only. */
export const PRESALE_FILE_TYPES = /^(application\/pdf|image\/(png|jpeg|webp|gif|heic))$/;
/** How many first bytes fileKind() reads. */
export const FILE_HEAD_BYTES = 16;

/** What a file is by its first bytes (its magic number): one of PRESALE_FILE_TYPES' types, or null. */
export function fileKind(head: Uint8Array): string | null {
  const at = (i: number, ...bytes: number[]) => bytes.every((b, k) => head[i + k] === b);
  const text = (i: number, s: string) => at(i, ...[...s].map((c) => c.charCodeAt(0)));
  if (text(0, "%PDF")) return "application/pdf";
  if (at(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  if (at(0, 0xff, 0xd8, 0xff)) return "image/jpeg";
  if (text(0, "GIF87a") || text(0, "GIF89a")) return "image/gif";
  if (text(0, "RIFF") && text(8, "WEBP")) return "image/webp";
  if (text(4, "ftyp") && ["heic", "heix", "hevc", "mif1", "msf1"].some((b) => text(8, b))) return "image/heic";
  return null;
}
export const PRESALE_FILE_HINT = "The payout statement or the summary invoice (a PDF or an image), never a backer list";

// Fields -----------------------------------------------------------------------------------------------------------

/** Free text in a pre-sale table: never contact details or PledgeBox order numbers. */
export const presaleText = (max = 200) => text(max);
const text = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .refine((s) => customerData(s, { orderNumbers: true }) !== NO_CONTACT, NO_CONTACT)
    .refine((s) => customerData(s, { orderNumbers: true }) !== NO_ORDER_NUMBERS, NO_ORDER_NUMBERS);
const isoDate = z.iso.date("Use a date like 2026-12-31");
const units = z.number().int("Whole units").min(0, "Units are 0 or more").max(10_000_000);
const currency = z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code");
const usdRate = z.number().positive("The rate to USD is more than 0").max(1_000_000);

export const presaleFields = z.object({
  run_id: z.uuid(),
  name: text(100).refine((s) => s.length > 0, "Name it (e.g. GCU2 Kickstarter)"),
  channels: z.array(z.enum(PRESALE_CHANNELS)).min(1, "Pick at least one channel").max(5),
  status: z.enum(PRESALE_STATUSES),
  ships_from: z.enum(SHIPS_FROM),
  as_of: isoDate.nullable(),
  backers_us: units,
  backers_intl: units,
  backers_unknown: units,
  order_tag: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9 _:.-]{0,60}$/, "A Shopify tag: letters, digits, spaces and - _ : . only"),
  /** What all its backers pledged, gross (USD), for each shipment's share of the deposits; null: not known. */
  pledge_total: z.number().min(0, "A pledge total is 0 or more").max(100_000_000).nullable(),
  notes: text(10_000),
});

export const presaleLineFields = z.object({
  presale_id: z.uuid(),
  item_id: z.uuid(),
  us_qty: units,
  intl_qty: units,
  unknown_qty: units,
  assigned_qty: units,
  not_shipping_qty: units,
  note: text(1000),
  position: z.number().int().min(0).max(100_000),
});

export const holdFields = z.object({
  run_id: z.uuid(),
  item_id: z.uuid(),
  qty: units,
  reason: text(300),
});

export const incomeFields = z.object({
  presale_id: z.uuid(),
  source: z.enum(INCOME_SOURCES),
  kind: z.enum(INCOME_KINDS),
  received_on: isoDate,
  amount: z.number().min(-100_000_000).max(100_000_000),
  currency,
  usd_rate: usdRate,
  reference: text(200),
  description: text(300),
  note: text(5000),
});

export const shipmentFields = z.object({
  presale_id: z.uuid(),
  shipped_on: isoDate,
  region: z.enum(REGIONS),
  packages: units,
  pledge_value: z.number().min(0, "A pledge value is 0 or more").max(100_000_000),
  note: text(5000),
});

export const shipmentCostInput = z.object({
  kind: z.enum(SHIPPING_KINDS),
  amount: z.number().min(-100_000_000).max(100_000_000),
  currency: currency.default("USD"),
  usd_rate: usdRate.optional(),
  vendor_id: z.uuid().nullable().optional(),
  description: text(500).optional(),
  invoice_number: text(100).optional(),
});

// Data -------------------------------------------------------------------------------------------------------------

/** The pre-sale tables (synced to staff only; receivers get none of them). */
export type PresaleData = {
  presales: readonly Presale[];
  lines: readonly PresaleLine[];
  holds: readonly ProductionRunHold[];
  income: readonly PresaleIncome[];
  shipments: readonly PresaleShipment[];
  shipmentLines: readonly PresaleShipmentLine[];
};

/** What the pre-sales are read against: the runs, what their POs make or buy, and where the CMs are. */
export type PresaleContext = {
  today: string;
  items: readonly Pick<Item, "id" | "sku" | "name" | "kind" | "source" | "shopify_product_id" | "shopify_product_title" | "shopify_variant_title">[];
  runs: readonly Pick<ProductionRun, "id" | "number" | "name" | "project_id" | "status" | "cm_vendor_id" | "target_ship" | "target_arrive">[];
  runLines: readonly Pick<ProductionRunLine, "run_id" | "item_id" | "qty">[];
  pos: readonly Pick<Po, "id" | "number" | "kind" | "status" | "production_run_id" | "vendor_id" | "expected_ship" | "expected_arrive">[];
  poLines: readonly (Pick<PoLine, "id" | "po_id" | "item_id" | "qty" | "qty_received" | "promised_date"> &
    Partial<Pick<PoLine, "qty_rejected" | "qty_direct" | "qty_direct_shipped" | "position">>)[];
  locations: readonly (Pick<Location, "id" | "kind" | "active" | "ignored"> & Partial<Pick<Location, "name" | "vendor_id" | "is_cm">>)[];
};

const day = (v: unknown) => (v == null || v === "" ? null : String(v).slice(0, 10));
const n = (v: unknown) => Number(v ?? 0) || 0;
const qtyText = (v: number) => v.toLocaleString("en-US", { maximumFractionDigits: 3 });
const usd = (cents: number) =>
  `${cents < 0 ? "−" : ""}$${(Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdUnit = (v: number) => `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(v) < 1 && v !== 0 ? 4 : 2 })}`;
const plural = (k: number, one: string, many = `${one}s`) => `${qtyText(k)} ${k === 1 ? one : many}`;

/** Units a line reserves: US + international + no country yet (assigned colours are among them; not shipping isn't). */
export const reservedOf = (l: Pick<PresaleLine, "us_qty" | "intl_qty" | "unknown_qty">) => n(l.us_qty) + n(l.intl_qty) + n(l.unknown_qty);
/** Backer boxes: US + international + no country yet. */
export const boxesOf = (p: Pick<Presale, "backers_us" | "backers_intl" | "backers_unknown">) => n(p.backers_us) + n(p.backers_intl) + n(p.backers_unknown);

/** An income entry in USD cents, signed: payouts, shipping collected and other (by its sign) in; fees and refunds out. */
export function incomeCents(e: Pick<PresaleIncome, "kind" | "amount" | "currency" | "usd_rate">): number {
  const rate = e.currency.toUpperCase() === "USD" ? 1 : n(e.usd_rate);
  const cents = Math.round(mulRound(mulRound(n(e.amount), rate, 8), 100, 0));
  return INCOME_OUT.has(e.kind) ? -Math.abs(cents) : cents;
}

export type PresaleIndex = ReturnType<typeof indexPresales>;

/** Lookups over the pre-sale rows and their context; build once per change. Views are cached on it. */
export function indexPresales(pd: PresaleData, ctx: PresaleContext) {
  const posByRun = groupBy(
    ctx.pos.filter((p) => p.production_run_id && p.status !== "cancelled"),
    (p) => p.production_run_id as string,
  );
  const shipmentById = new Map(pd.shipments.map((s) => [s.id, s]));
  return {
    pd,
    ctx,
    today: ctx.today,
    itemById: new Map(ctx.items.map((i) => [i.id, i])),
    runById: new Map(ctx.runs.map((r) => [r.id, r])),
    presaleById: new Map(pd.presales.map((p) => [p.id, p])),
    presalesByRun: groupBy(
      [...pd.presales].sort((a, b) => a.created_at.localeCompare(b.created_at)),
      (p) => p.run_id,
    ),
    linesByPresale: groupBy(
      [...pd.lines].sort((a, b) => a.position - b.position),
      (l) => l.presale_id,
    ),
    holdsByRun: groupBy(pd.holds, (h) => h.run_id),
    incomeByPresale: groupBy(
      [...pd.income].sort((a, b) => String(a.received_on).localeCompare(String(b.received_on)) || a.created_at.localeCompare(b.created_at)),
      (e) => e.presale_id,
    ),
    shipmentsByPresale: groupBy(
      [...pd.shipments].sort((a, b) => String(a.shipped_on).localeCompare(String(b.shipped_on)) || a.created_at.localeCompare(b.created_at)),
      (s) => s.presale_id,
    ),
    shipLinesByShipment: groupBy(pd.shipmentLines, (l) => l.shipment_id),
    shipmentById,
    posByRun,
    poById: new Map(ctx.pos.map((p) => [p.id, p])),
    linesByPo: groupBy(
      [...ctx.poLines].sort((a, b) => n(a.position) - n(b.position)),
      (l) => l.po_id,
    ),
    runLinesByRun: groupBy(ctx.runLines, (l) => l.run_id),
    cache: new Map<string, RunPresales>(),
  };
}

/** A CM's location: its consignment CM site, else its first vendor site (planning's rule). */
export function cmSiteOf(ctx: Pick<PresaleContext, "locations">, vendorId: string | null | undefined): string | null {
  if (!vendorId) return null;
  const site = ctx.locations
    .filter((l) => l.kind === "vendor_site" && l.vendor_id === vendorId && l.active && !l.ignored)
    .sort((a, b) => Number(!!b.is_cm) - Number(!!a.is_cm) || (a.name ?? "").localeCompare(b.name ?? ""))[0];
  return site?.id ?? null;
}

// A run's pre-sales, per item ----------------------------------------------------------------------------------------

export type ItemRole = "made" | "bought" | "neither";

export type RunItemRow = {
  itemId: string;
  sku: string;
  name: string;
  /** made: the run builds it (its make PO, else its plan); bought: the run's other POs buy it; neither: not on the run. */
  role: ItemRole;
  /** What the run makes or buys of it, and where that number comes from. */
  made: number;
  madeText: string;
  /** Reserved on the run's live pre-sales: US, international, no country yet; assigned colours among them. */
  reserved: number;
  us: number;
  intl: number;
  unknown: number;
  assigned: number;
  notShipping: number;
  shipped: number;
  /** reserved − shipped, never below 0. */
  remaining: number;
  /** Shipped beyond what's reserved. */
  over: number;
  /** Of the reserved, on pre-sales that ship from the CM (never at HHL). */
  fromCm: number;
  /** Units on the run's make PO lines that go straight to backers (po_line.qty_direct). */
  direct: number;
  held: number;
  holds: ProductionRunHold[];
  /** What's left to sell: made − (reserved, or shipped if more) − held. Negative = short. */
  retailLeft: number;
  /** Plain-words problems: reserved more than made, reserved not set aside on the make PO… (red in the table). */
  flags: string[];
  /** The make PO lines it's on (with their units for HHL). */
  makeLines: { po: string; poId: string; qty: number; direct: number; forHhl: number; received: number }[];
};

export type ShipmentState = {
  shipment: PresaleShipment;
  lines: PresaleShipmentLine[];
  units: number;
  month: string;
};

export type PresaleState = {
  presale: Presale;
  live: boolean;
  lines: (PresaleLine & { reserved: number; shipped: number; remaining: number; over: number })[];
  reserved: number;
  shipped: number;
  remaining: number;
  boxes: number;
  shipments: ShipmentState[];
  income: PresaleIncome[];
  pledgeShipped: number;
};

export type RunPresales = {
  runId: string;
  presales: PresaleState[];
  rows: RunItemRow[];
  holds: ProductionRunHold[];
  totals: { reserved: number; shipped: number; remaining: number; held: number; boxes: number };
  /** "924 of 1,701 GCU130x pre-sold": per product the run makes, for the run's header. */
  chips: { key: string; label: string; reserved: number; made: number; held: number; text: string; why: string }[];
  flags: string[];
};

const EMPTY_RUN = (runId: string): RunPresales => ({
  runId,
  presales: [],
  rows: [],
  holds: [],
  totals: { reserved: 0, shipped: 0, remaining: 0, held: 0, boxes: 0 },
  chips: [],
  flags: [],
});

/** "GCU130x" for GCU1300…GCU1304: the SKUs' common start, the rest as x. */
export function skuFamily(skus: string[]): string {
  if (!skus.length) return "";
  if (skus.length === 1) return skus[0];
  let k = 0;
  while (skus.every((s) => s[k] !== undefined && s[k] === skus[0][k])) k++;
  const longest = Math.max(...skus.map((s) => s.length));
  return k ? `${skus[0].slice(0, k)}${"x".repeat(Math.max(1, longest - k))}` : skus.join(", ");
}

/** Everything a run's pre-sales and holds come to, per item. Cached on the index. */
export function runPresales(ix: PresaleIndex, runId: string): RunPresales {
  const hit = ix.cache.get(runId);
  if (hit) return hit;
  const run = ix.runById.get(runId);
  const presales = ix.presalesByRun.get(runId) ?? [];
  const holds = (ix.holdsByRun.get(runId) ?? []).filter((h) => n(h.qty) > 0 || h.reason);
  if (!run || (!presales.length && !holds.length)) {
    const empty = EMPTY_RUN(runId);
    ix.cache.set(runId, empty);
    return empty;
  }
  const states: PresaleState[] = presales.map((p) => {
    const live = presaleLive(p.status);
    const shipments = (ix.shipmentsByPresale.get(p.id) ?? []).map((s) => {
      const lines = ix.shipLinesByShipment.get(s.id) ?? [];
      return { shipment: s, lines, units: lines.reduce((t, l) => t + n(l.qty), 0), month: String(s.shipped_on).slice(0, 7) };
    });
    const shippedOf = new Map<string, number>();
    for (const s of shipments) for (const l of s.lines) shippedOf.set(l.item_id, (shippedOf.get(l.item_id) ?? 0) + n(l.qty));
    const lines = (ix.linesByPresale.get(p.id) ?? []).map((l) => {
      const reserved = reservedOf(l);
      const shipped = shippedOf.get(l.item_id) ?? 0;
      return { ...l, reserved, shipped, remaining: Math.max(0, reserved - shipped), over: Math.max(0, shipped - reserved) };
    });
    // Shipped items with no reservation line still count as shipped.
    for (const [itemId, shipped] of shippedOf)
      if (!lines.some((l) => l.item_id === itemId))
        lines.push({
          id: `shipped:${p.id}:${itemId}`,
          presale_id: p.id,
          item_id: itemId,
          us_qty: 0,
          intl_qty: 0,
          unknown_qty: 0,
          assigned_qty: 0,
          not_shipping_qty: 0,
          note: "",
          position: 100_000,
          created_at: "",
          updated_at: "",
          reserved: 0,
          shipped,
          remaining: 0,
          over: shipped,
        });
    const sum = (k: "reserved" | "shipped" | "remaining") => lines.reduce((t, l) => t + l[k], 0);
    return {
      presale: p,
      live,
      lines,
      reserved: sum("reserved"),
      shipped: sum("shipped"),
      remaining: sum("remaining"),
      boxes: boxesOf(p),
      shipments,
      income: ix.incomeByPresale.get(p.id) ?? [],
      pledgeShipped: shipments.reduce((t, s) => t + n(s.shipment.pledge_value), 0),
    };
  });

  // What the run makes and buys, per item.
  const pos = ix.posByRun.get(runId) ?? [];
  const makeLines = new Map<string, RunItemRow["makeLines"]>();
  const bought = new Map<string, { qty: number; pos: Set<string> }>();
  for (const po of [...pos].sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }))) {
    for (const l of ix.linesByPo.get(po.id) ?? []) {
      if (po.kind === "make") {
        const list = makeLines.get(l.item_id) ?? [];
        const direct = n(l.qty_direct);
        list.push({ po: po.number, poId: po.id, qty: n(l.qty), direct, forHhl: Math.max(0, n(l.qty) - direct), received: n(l.qty_received) });
        makeLines.set(l.item_id, list);
      } else {
        const b = bought.get(l.item_id) ?? { qty: 0, pos: new Set<string>() };
        b.qty += n(l.qty);
        b.pos.add(po.number);
        bought.set(l.item_id, b);
      }
    }
  }
  const planned = new Map<string, number>();
  for (const l of ix.runLinesByRun.get(runId) ?? []) planned.set(l.item_id, (planned.get(l.item_id) ?? 0) + n(l.qty));

  const ids = new Set<string>();
  for (const s of states) for (const l of s.lines) ids.add(l.item_id);
  for (const h of holds) ids.add(h.item_id);
  const rows: RunItemRow[] = [];
  for (const itemId of ids) {
    const item = ix.itemById.get(itemId);
    // Every pre-sale but a cancelled one: a done one's units went to backers all the same.
    const counted = states.filter((s) => s.presale.status !== "cancelled");
    const mine = counted.flatMap((s) => s.lines.filter((l) => l.item_id === itemId).map((l) => ({ s, l })));
    const add = (pick: (x: (typeof mine)[number]) => number) => mine.reduce((t, x) => t + pick(x), 0);
    const reserved = add((x) => x.l.reserved);
    const shipped = add((x) => x.l.shipped);
    const fromCm = add((x) => (x.s.presale.ships_from === "cm" ? x.l.reserved : 0));
    const ml = makeLines.get(itemId) ?? [];
    const b = bought.get(itemId);
    const plannedQty = planned.get(itemId) ?? 0;
    const role: ItemRole = ml.length || plannedQty > 0 ? "made" : b ? "bought" : "neither";
    const made = ml.length ? ml.reduce((t, l) => t + l.qty, 0) : role === "made" ? plannedQty : (b?.qty ?? 0);
    const madeText = ml.length
      ? ml.map((l) => `${l.po}: ${qtyText(l.qty)}`).join(", ")
      : role === "made"
        ? `${qtyText(plannedQty)} planned (no make PO yet)`
        : b
          ? `Bought on ${[...b.pos].join(", ")}`
          : "Not on this run's plan or POs";
    const itemHolds = holds.filter((h) => h.item_id === itemId);
    const held = itemHolds.reduce((t, h) => t + n(h.qty), 0);
    const direct = ml.reduce((t, l) => t + l.direct, 0);
    const taken = Math.max(reserved, shipped);
    const retailLeft = made - taken - held;
    const flags: string[] = [];
    if (taken + held > made)
      flags.push(
        role === "neither"
          ? `${qtyText(taken + held)} promised, but this run neither makes nor buys it`
          : `${qtyText(taken + held)} ${held ? "reserved and held" : "reserved"}, but the run ${role === "made" ? "makes" : "buys"} only ${qtyText(made)}: short by ${qtyText(taken + held - made)}`,
      );
    const cmTaken = Math.max(
      fromCm,
      add((x) => (x.s.presale.ships_from === "cm" ? x.l.shipped : 0)),
    );
    if (ml.length && cmTaken > direct && taken <= made)
      flags.push(
        `${qtyText(cmTaken - direct)} of the units for backers aren't set aside on ${ml.map((l) => l.po).join(", ")} (they were received at HHL already): ship them from HHL's stock or lower the reservation`,
      );
    const over = add((x) => x.l.over);
    if (over > 0) flags.push(`${qtyText(over)} shipped beyond what's reserved`);
    rows.push({
      itemId,
      sku: item?.sku ?? "",
      name: item?.shopify_variant_title || item?.name || "",
      role,
      made,
      madeText,
      reserved,
      us: add((x) => n(x.l.us_qty)),
      intl: add((x) => n(x.l.intl_qty)),
      unknown: add((x) => n(x.l.unknown_qty)),
      assigned: add((x) => n(x.l.assigned_qty)),
      notShipping: add((x) => n(x.l.not_shipping_qty)),
      shipped,
      remaining: Math.max(0, reserved - shipped),
      over,
      fromCm,
      direct,
      held,
      holds: itemHolds,
      retailLeft,
      flags,
      makeLines: ml,
    });
  }
  const roleOrder: Record<ItemRole, number> = { made: 0, bought: 1, neither: 2 };
  rows.sort((a, b) => roleOrder[a.role] - roleOrder[b.role] || a.sku.localeCompare(b.sku, undefined, { numeric: true }));

  // Chips: per product the run makes (its variants together), reserved of made.
  const chips: RunPresales["chips"] = [];
  const byProduct = groupBy(
    rows.filter((r) => r.role === "made" && r.made > 0),
    (r) => ix.itemById.get(r.itemId)?.shopify_product_id ?? r.itemId,
  );
  for (const [key, list] of byProduct) {
    const reserved = list.reduce((t, r) => t + Math.max(r.reserved, r.shipped), 0);
    const made = list.reduce((t, r) => t + r.made, 0);
    const held = list.reduce((t, r) => t + r.held, 0);
    if (!reserved && !held) continue;
    const label = skuFamily(list.map((r) => r.sku).sort());
    const title = ix.itemById.get(list[0].itemId)?.shopify_product_title || list[0].name;
    chips.push({
      key: String(key),
      label,
      reserved,
      made,
      held,
      text: `${qtyText(reserved)} of ${qtyText(made)} ${label} pre-sold${held ? `, ${qtyText(held)} held` : ""}`,
      why: `${title}: ${list.map((r) => `${r.sku} ${qtyText(Math.max(r.reserved, r.shipped))} of ${qtyText(r.made)}`).join(" · ")}. ${qtyText(made - reserved - held)} left for retail.`,
    });
  }
  const liveStates = states.filter((s) => s.presale.status !== "cancelled");
  const out: RunPresales = {
    runId,
    presales: states,
    rows,
    holds,
    totals: {
      reserved: liveStates.reduce((t, s) => t + s.reserved, 0),
      shipped: states.reduce((t, s) => t + s.shipped, 0),
      remaining: liveStates.reduce((t, s) => t + s.remaining, 0),
      held: holds.reduce((t, h) => t + n(h.qty), 0),
      boxes: liveStates.reduce((t, s) => t + s.boxes, 0),
    },
    chips,
    flags: rows.flatMap((r) => r.flags.map((f) => `${r.sku}: ${f}`)),
  };
  ix.cache.set(runId, out);
  return out;
}

// Planning ---------------------------------------------------------------------------------------------------------

/** When a run's goods are due at HHL: its make PO line's promised date, the PO's arrival or ship date, the run's arrival. */
function arrivalOf(ix: PresaleIndex, runId: string, itemId: string | null): string | null {
  const run = ix.runById.get(runId);
  for (const po of ix.posByRun.get(runId) ?? []) {
    if (po.kind !== "make") continue;
    const line = (ix.linesByPo.get(po.id) ?? []).find((l) => !itemId || l.item_id === itemId);
    if (!line) continue;
    const d = day(line.promised_date) ?? day(po.expected_arrive) ?? day(po.expected_ship);
    if (d) return d;
  }
  return day(run?.target_arrive) ?? day(run?.target_ship);
}

/** When the CM packs the backers' boxes: the run's ship date, else its make PO's, else its arrival. */
function packDayOf(ix: PresaleIndex, runId: string): string | null {
  const run = ix.runById.get(runId);
  const make = (ix.posByRun.get(runId) ?? []).find((p) => p.kind === "make");
  return day(run?.target_ship) ?? day(make?.expected_ship) ?? arrivalOf(ix, runId, null);
}

/** Units of an item received at HHL from the run's POs (what a hold or an HHL pre-sale can already be sitting in). */
function receivedFromRun(ix: PresaleIndex, runId: string, itemId: string) {
  let got = 0;
  for (const po of ix.posByRun.get(runId) ?? []) for (const l of ix.linesByPo.get(po.id) ?? []) if (l.item_id === itemId) got += n(l.qty_received);
  return got;
}

/**
 * What planning must not count as for sale, per item, as demand on a day (shared/planning.ts nets it like B2B):
 * - backers (a pre-sale shipping from the CM): for what the run makes, only what its make PO lines don't already set
 *   aside (qty_direct takes them out of the PO's supply): none once the make PO covers them, all of it before there is
 *   one (they still need building); for parts and add-ons, what's still to ship, needed at the run's CM site
 * - presale (shipping from HHL) and held: at HHL, on the day the run's goods arrive (today for what has arrived)
 * One row per run, item and kind, also when nothing is left to plan (qty 0), so explain() can say why.
 */
/**
 * Shopify products a pre-sale is selling (decision 7's exception): what any pre-sale that isn't cancelled reserves, and
 * what its run makes, on any run, done ones too. Every variant of them is planned, even one with no reservation:
 * a "Funded with Kickstarter" title then says nothing about which units are for sale.
 */
export function presaleProducts(ix: PresaleIndex): string[] {
  const out = new Set<string>();
  const add = (itemId: string) => {
    const p = ix.itemById.get(itemId)?.shopify_product_id;
    if (p) out.add(p);
  };
  for (const p of ix.pd.presales) {
    if (p.status === "cancelled") continue;
    for (const l of ix.linesByPresale.get(p.id) ?? []) add(l.item_id);
    for (const l of ix.runLinesByRun.get(p.run_id) ?? []) add(l.item_id);
  }
  return [...out].sort();
}

export function planReservations(ix: PresaleIndex): PlanReservation[] {
  const out: PlanReservation[] = [];
  const today = ix.today;
  const later = (d: string | null) => (d && d > today ? d : today);
  for (const run of ix.ctx.runs) {
    const rp = runPresales(ix, run.id);
    if (!rp.rows.length) continue;
    const site = cmSiteOf(ix.ctx, run.cm_vendor_id);
    const cmName = site ? ix.ctx.locations.find((l) => l.id === site)?.name || "the CM" : "the CM";
    for (const row of rp.rows) {
      const live = rp.presales.filter((s) => s.live);
      const lines = (from: "cm" | "hhl") =>
        live.filter((s) => s.presale.ships_from === from).flatMap((s) => s.lines.filter((l) => l.item_id === row.itemId).map((l) => ({ s, l })));
      // Backers: never at HHL.
      const cm = lines("cm");
      if (cm.length) {
        const reserved = cm.reduce((t, x) => t + x.l.reserved, 0);
        const shipped = cm.reduce((t, x) => t + x.l.shipped, 0);
        const label =
          cm
            .map((x) => x.s.presale.name)
            .filter((v, i, a) => a.indexOf(v) === i)
            .join(" + ") || "the pre-sale";
        if (row.role === "made") {
          const qty = Math.max(0, reserved - Math.max(row.direct, shipped));
          out.push({
            itemId: row.itemId,
            kind: "backers",
            label,
            runId: run.id,
            runNumber: run.number,
            reserved,
            shipped,
            direct: row.direct,
            qty,
            day: later(arrivalOf(ix, run.id, row.itemId)),
            site: null,
            why: row.makeLines.length
              ? `${qtyText(row.direct)} of ${row.makeLines.map((l) => `${l.po}'s ${qtyText(l.qty)}`).join(", ")} go straight to backers from ${cmName}, so only ${qtyText(row.makeLines.reduce((t, l) => t + l.forHhl, 0))} are expected at HHL${qty ? `; ${qtyText(qty)} more still need building for them` : ""}`
              : `No make PO yet: all ${qtyText(qty)} still need building`,
          });
        } else {
          const qty = Math.max(0, reserved - shipped);
          out.push({
            itemId: row.itemId,
            kind: "backers",
            label,
            runId: run.id,
            runNumber: run.number,
            reserved,
            shipped,
            direct: 0,
            qty,
            day: later(packDayOf(ix, run.id)),
            site,
            why: qty ? `${qtyText(qty)} still to pack into backers' boxes at ${cmName}` : "All of it has shipped",
          });
        }
      }
      // HHL pre-sales and holds: they arrive at HHL with the rest; whatever has arrived is in stock now.
      let pool = receivedFromRun(ix, run.id, row.itemId);
      const arrive = later(arrivalOf(ix, run.id, row.itemId));
      const atHhl = (kind: "presale" | "held", label: string, reserved: number, shipped: number, qty: number, why: string) => {
        if (reserved <= 0 && qty <= 0) return;
        const here = Math.min(qty, Math.max(0, pool));
        pool -= here;
        const base = { itemId: row.itemId, kind, label, runId: run.id, runNumber: run.number, reserved, shipped, direct: 0, site: null };
        if (here > 0) out.push({ ...base, qty: here, day: today, why: `${why}: ${qtyText(here)} in stock already` });
        if (qty - here > 0 || here === 0) out.push({ ...base, qty: qty - here, day: arrive, why });
      };
      const hhl = lines("hhl");
      if (hhl.length) {
        const reserved = hhl.reduce((t, x) => t + x.l.reserved, 0);
        const shipped = hhl.reduce((t, x) => t + x.l.shipped, 0);
        const label = hhl
          .map((x) => x.s.presale.name)
          .filter((v, i, a) => a.indexOf(v) === i)
          .join(" + ");
        atHhl("presale", label, reserved, shipped, Math.max(0, reserved - shipped), `${qtyText(Math.max(0, reserved - shipped))} to ship from HHL`);
      }
      for (const h of row.holds) atHhl("held", h.reason || "held", n(h.qty), 0, n(h.qty), "They arrive at HHL with the rest; lower the hold to release them");
    }
  }
  return out;
}

/** Stock at HHL held out of what's for sale (holds and HHL pre-sales whose units have arrived), for Forecast → Health. */
export function heldAtHhl(reservations: readonly PlanReservation[], today: string): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of reservations) if (r.kind !== "backers" && r.day <= today && r.qty > 0) out.set(r.itemId, (out.get(r.itemId) ?? 0) + r.qty);
  return out;
}

/** What's set aside at a CM for backers' boxes (parts and add-ons still to pack): consignment's "for backers". */
export function cmClaims(reservations: readonly PlanReservation[]): ConsignClaim[] {
  return reservations
    .filter((r) => r.kind === "backers" && r.site && r.qty > 0)
    .map((r) => ({ location_id: r.site as string, item_id: r.itemId, qty: r.qty, label: `${r.label} (${r.runNumber})` }));
}

// The result ---------------------------------------------------------------------------------------------------------

export type UnitCost = {
  usd: number | null;
  source: "actual" | "planned" | "run_po" | "landed" | "price" | "missing";
  text: string;
  /** What the run makes: the freight in (to the US) and US duty in `usd`, per unit. An international backer's unit has none. */
  inboundUsd?: number;
};

/**
 * Mitch, 2026-10-29: international backers' units go from the CM to the DDP forwarder, whose freight and duty are in
 * shipping to backers; so they're costed without the run's freight in and US duty. US units (and those with no country
 * yet) carry them: they're imported to Philadelphia with the retail goods.
 */
export function intlUnitCost(c: UnitCost): UnitCost {
  if (c.usd == null || !c.inboundUsd) return c;
  return {
    ...c,
    usd: Math.max(0, c.usd - c.inboundUsd),
    inboundUsd: 0,
    text: `${c.text}; international: less its freight in and US duty (${usdUnit(c.inboundUsd)}), since it goes from the CM to the DDP forwarder and that freight and duty are in shipping to backers`,
  };
}

/**
 * International backers' units of an item on a run (every pre-sale that ships from the CM): reserved for international
 * backers, or shipped to them if more (a cancelled pre-sale: what it shipped). They never come into the US, so the run's
 * freight in and duty fall on its other units (knownUnitCost()'s `intlOf`).
 */
export function runIntlUnits(ix: PresaleIndex, runId: string, itemId: string): number {
  let total = 0;
  for (const p of ix.presalesByRun.get(runId) ?? []) {
    if (p.ships_from !== "cm") continue;
    let shipped = 0;
    for (const sh of ix.shipmentsByPresale.get(p.id) ?? [])
      if (sh.region === "intl") for (const l of ix.shipLinesByShipment.get(sh.id) ?? []) if (l.item_id === itemId) shipped += n(l.qty);
    const reserved =
      p.status === "cancelled" ? 0 : (ix.linesByPresale.get(p.id) ?? []).filter((l) => l.item_id === itemId).reduce((t, l) => t + n(l.intl_qty), 0);
    total += Math.max(reserved, shipped);
  }
  return total;
}

/**
 * What one unit of an item costs for a run's pre-sale: actual (built) or planned landed cost, else what was paid.
 * `intlOf`: the run's international backers' units of each variant (runIntlUnits()), which carry no freight in or US
 * duty (`intl`: of this item only).
 */
export function presaleUnitCost(
  cx: CostIndex,
  runId: string,
  itemId: string,
  units: number,
  opts: { intl?: number; intlOf?: (variantId: string) => number } = {},
): UnitCost {
  const run = cx.runById.get(runId);
  const res = runResult(cx, runId);
  if (res?.variants.includes(itemId)) {
    // Each layer actual where the run has it, planned where it doesn't yet (freight and duty before the landed cost).
    const k = knownUnitCost(res, itemId, opts);
    if (k?.built)
      return {
        usd: k.cents / 100,
        source: "actual",
        inboundUsd: k.inboundCents / 100,
        text: `Landed cost per good unit on ${run?.number}: ${usdUnit(k.cents / 100)}, actual${k.plannedParts.length ? `, but ${k.plannedParts.join(" and ")} still planned` : ""}${k.notes.length ? `; ${k.notes.join("; ")}` : ""}`,
      };
    if (k)
      return {
        usd: k.cents / 100,
        source: "planned",
        inboundUsd: k.inboundCents / 100,
        text: `Planned landed cost on ${run?.number}: ${usdUnit(k.cents / 100)} (nothing built yet)`,
      };
  }
  const paid = runBoughtPrice(cx, runId, itemId);
  if (paid.unit != null)
    return {
      usd: paid.unit,
      source: "run_po",
      text: `What ${run?.number}'s POs paid: ${paid.lines.map((l) => `${l.po} ${qtyText(l.qty)} @ ${usdUnit(l.unit)}`).join(", ")}`,
    };
  // A sub-assembly reward (the dongle) has no project of its own: the run's.
  const landed = landedCostOf(cx, itemId, run?.project_id);
  if (landed) return { usd: landed.cost, source: "landed", text: landed.text };
  const price = plannedPrice(cx, itemId, units, basisOf(run));
  if (price.unit != null) return { usd: price.unit, source: "price", text: price.text };
  return { usd: null, source: "missing", text: price.text };
}

export type PresaleResult = {
  presale: Presale;
  state: PresaleState;
  income: {
    /** Payouts, shipping collected and other money in (cents). */
    inCents: number;
    feesCents: number;
    refundsCents: number;
    netCents: number;
    bySource: { source: string; cents: number; entries: number }[];
    entries: { entry: PresaleIncome; cents: number }[];
  };
  units: {
    /** `intl`: of `units`, international backers' (costed without freight in and US duty). */
    lines: { itemId: string; sku: string; units: number; intl: number; cost: UnitCost; cents: number }[];
    cents: number;
    missing: string[];
    count: number;
  };
  shipping: { cents: number; byKind: { kind: string; label: string; cents: number }[]; costs: ProductionCost[] };
  resultCents: number;
  /** A cancelled pre-sale: its units aren't counted at landed cost (nothing ships for it). */
  cancelled: boolean;
  boxes: number;
  perBoxCents: number | null;
  /** The units per-unit divides by: the units of what the run makes (the controllers), else every unit. */
  perUnitCount: number;
  perUnitCents: number | null;
  /** What the shipped backers paid (pledge value, USD cents): recognised so far. */
  pledgeShippedCents: number;
  why: Record<"income" | "fees" | "refunds" | "net" | "units" | "shipping" | "result" | "perBox" | "perUnit" | "pledge", string>;
};

/** The shipping costs a pre-sale carries: its own, and on a run with one pre-sale, the run's backer shipping not tied to one. */
export function presaleShippingCosts(cx: CostIndex, ix: PresaleIndex, presaleId: string): ProductionCost[] {
  const p = ix.presaleById.get(presaleId);
  if (!p) return [];
  const only = (ix.presalesByRun.get(p.run_id) ?? []).length === 1;
  return (cx.costsByRun.get(p.run_id) ?? []).filter((c) => c.category === "backer_shipping" && (c.presale_id === p.id || (!c.presale_id && only)));
}

/**
 * A pre-sale's result: income (as paid out) − its units at landed cost (reserved, or shipped if more; actual once
 * built, else planned; add-ons at what the run paid) − shipping to backers, with per backer box and per unit.
 */
export function presaleResult(cx: CostIndex, ix: PresaleIndex, presaleId: string): PresaleResult | null {
  const p = ix.presaleById.get(presaleId);
  if (!p) return null;
  const rp = runPresales(ix, p.run_id);
  const state = rp.presales.find((s) => s.presale.id === p.id);
  if (!state) return null;
  const entries = state.income.map((entry) => ({ entry, cents: incomeCents(entry) }));
  const inCents = entries.filter((e) => !INCOME_OUT.has(e.entry.kind)).reduce((t, e) => t + e.cents, 0);
  const feesCents = -entries.filter((e) => e.entry.kind === "fees").reduce((t, e) => t + e.cents, 0);
  const refundsCents = -entries.filter((e) => e.entry.kind === "refund").reduce((t, e) => t + e.cents, 0);
  const netCents = inCents - feesCents - refundsCents;
  const bySource = [...groupBy(entries, (e) => e.entry.source)].map(([source, list]) => ({
    source: INCOME_SOURCE_LABELS[source as IncomeSource] ?? source,
    cents: list.reduce((t, e) => t + e.cents, 0),
    entries: list.length,
  }));

  // A cancelled pre-sale ships nothing: only what it did ship counts.
  const cancelled = p.status === "cancelled";
  // International units: reserved for international backers, or shipped to them if more (intlUnitCost()).
  const intlShipped = new Map<string, number>();
  for (const sh of state.shipments)
    if (sh.shipment.region === "intl") for (const l of sh.lines) intlShipped.set(l.item_id, (intlShipped.get(l.item_id) ?? 0) + n(l.qty));
  const lines = state.lines
    .filter((l) => (cancelled ? l.shipped : Math.max(l.reserved, l.shipped)) > 0)
    .map((l) => {
      const unitsOf = cancelled ? l.shipped : Math.max(l.reserved, l.shipped);
      const full = presaleUnitCost(cx, p.run_id, l.item_id, unitsOf, { intlOf: (v) => runIntlUnits(ix, p.run_id, v) });
      const intl = Math.min(unitsOf, cancelled ? (intlShipped.get(l.item_id) ?? 0) : Math.max(n(l.intl_qty), intlShipped.get(l.item_id) ?? 0));
      const there = intl > 0 && full.inboundUsd ? intlUnitCost(full) : null;
      const cents =
        full.usd == null ? 0 : Math.round(mulRound(unitsOf - intl, full.usd * 100, 4)) + Math.round(mulRound(intl, (there?.usd ?? full.usd) * 100, 4));
      const cost: UnitCost = there
        ? {
            ...full,
            text: `${full.text}; of the ${qtyText(unitsOf)}, ${qtyText(intl)} international at ${usdUnit(there.usd ?? 0)}: without the freight in and US duty (${usdUnit(full.inboundUsd ?? 0)}), since they go from the CM to the DDP forwarder and that freight and duty are in shipping to backers`,
          }
        : full;
      return {
        itemId: l.item_id,
        sku: ix.itemById.get(l.item_id)?.sku ?? cx.itemById.get(l.item_id)?.sku ?? "",
        units: unitsOf,
        intl: there ? intl : 0,
        cost,
        cents,
      };
    });
  const unitCents = lines.reduce((t, l) => t + l.cents, 0);
  const missing = lines.filter((l) => l.cost.usd == null).map((l) => l.sku);
  const costs = presaleShippingCosts(cx, ix, p.id);
  const kinds = new Map<string, number>();
  for (const c of costs) kinds.set(c.shipping_kind || "other", (kinds.get(c.shipping_kind || "other") ?? 0) + costCents(c, 0));
  const shippingCents = [...kinds.values()].reduce((t, c) => t + c, 0);
  const resultCents = netCents - unitCents - shippingCents;
  const boxes = state.boxes;
  const made = new Set(rp.rows.filter((r) => r.role === "made").map((r) => r.itemId));
  const madeUnits = lines.filter((l) => made.has(l.itemId)).reduce((t, l) => t + l.units, 0);
  const perUnitCount = madeUnits || lines.reduce((t, l) => t + l.units, 0);
  const pledgeShippedCents = Math.round(mulRound(state.pledgeShipped, 100, 0));
  const count = lines.reduce((t, l) => t + l.units, 0);
  return {
    presale: p,
    state,
    income: { inCents, feesCents, refundsCents, netCents, bySource, entries },
    units: { lines, cents: unitCents, missing, count },
    shipping: {
      cents: shippingCents,
      byKind: [...kinds].map(([kind, cents]) => ({ kind, label: SHIPPING_KIND_LABELS[kind as ShippingKind] ?? kind, cents })),
      costs,
    },
    resultCents,
    cancelled,
    boxes,
    perBoxCents: boxes > 0 ? Math.round(resultCents / boxes) : null,
    perUnitCount,
    perUnitCents: perUnitCount > 0 ? Math.round(resultCents / perUnitCount) : null,
    pledgeShippedCents,
    why: {
      income: `${plural(entries.filter((e) => !INCOME_OUT.has(e.entry.kind)).length, "entry", "entries")}: payouts as paid out (their fees already taken off), shipping collected and other money in${bySource.length ? `: ${bySource.map((s) => `${s.source} ${usd(s.cents)}`).join(", ")}` : ""}`,
      fees: feesCents ? "Fees taken separately (a payout's own fees are already out of it)" : "No fees entered on their own",
      refunds: refundsCents ? "Money given back to backers" : "No refunds",
      net: "Money in − fees − refunds",
      units: cancelled
        ? `Cancelled: only what it shipped counts (${plural(count, "unit")})`
        : `${plural(count, "unit")} (reserved, or shipped if more) × each one's landed cost: actual where the run has it, planned where it doesn't yet; international backers' units without the freight in and US duty (theirs are in shipping to backers); add-ons at what the run's POs paid${missing.length ? `. No cost yet for ${missing.join(", ")} (counted as $0)` : ""}`,
      shipping: costs.length
        ? `${plural(costs.length, "cost")} of category Shipping to backers: ${[...kinds].map(([k, c]) => `${SHIPPING_KIND_LABELS[k as ShippingKind] ?? k} ${usd(c)}`).join(", ")}`
        : "No shipping to backers recorded yet",
      result: "Net income − units at landed cost − shipping to backers",
      perBox: boxes > 0 ? `${usd(resultCents)} ÷ ${plural(boxes, "backer box", "backer boxes")}` : "No backer count yet",
      perUnit:
        perUnitCount > 0
          ? `${usd(resultCents)} ÷ ${plural(perUnitCount, "unit")}${madeUnits ? " of what the run makes (add-ons not counted)" : ""}`
          : "No units reserved",
      pledge: `What the backers shipped so far pledged (${plural(state.shipments.length, "shipment")}), before the platforms' fees: for reference. The bookkeeper pack's share of deposits is the amount to recognise`,
    },
  };
}

// Bookkeeper pack ----------------------------------------------------------------------------------------------------

export type PresalePackFile = Pick<Attachment, "id" | "owner_type" | "owner_id" | "kind" | "filename" | "content_type">;

export type PresalePack = {
  income: { presale: Presale; run: string; entry: PresaleIncome; cents: number; files: PresalePackFile[] }[];
  shipments: {
    presale: Presale;
    run: string;
    shipment: PresaleShipment;
    units: number;
    byItem: { sku: string; qty: number }[];
    /** The units at landed cost as they were when the shipment was recorded (presale_shipment_line.unit_cost). */
    unitCostCents: number;
    missingCost: string[];
    costs: Record<ShippingKind, number>;
    shippingCents: number;
    /** Its share of the deposits: pledge value × its deposit_ratio (kept when recorded); null: no total pledge value then. */
    depositShareCents: number | null;
    /** Bills its shipping costs are on already (not to be booked twice). */
    bills: string[];
    /** What was filled in after it was recorded, and when (presale_shipment.fill_costs). */
    filledLater: string[];
  }[];
  totals: { inCents: number; outCents: number; pledgeCents: number; unitCostCents: number; shippingCents: number; depositShareCents: number };
  /** Pre-sales shipped in the month whose total pledge value isn't known (no share of deposits for them). */
  unknownPledge: string[];
};

/** The month's pre-sale income entries and shipments to backers, for the bookkeeper pack (presales.csv, presale_shipments.csv). */
export function presalePack(
  cx: CostIndex,
  ix: PresaleIndex,
  from: string,
  to: string,
  files: readonly PresalePackFile[] = [],
  billsByCost: ReadonlyMap<string, string[]> = new Map(),
): PresalePack {
  const inMonth = (d: unknown) => {
    const v = day(d);
    return !!v && v >= from && v <= to;
  };
  const runOf = (p: Presale) => ix.runById.get(p.run_id)?.number ?? "";
  const filesOf = groupBy(
    files.filter((f) => f.owner_type === "presale_income"),
    (f) => f.owner_id,
  );
  const income = [...ix.pd.income]
    .filter((e) => inMonth(e.received_on))
    .sort((a, b) => String(a.received_on).localeCompare(String(b.received_on)) || a.created_at.localeCompare(b.created_at))
    .flatMap((entry) => {
      const presale = ix.presaleById.get(entry.presale_id);
      return presale ? [{ presale, run: runOf(presale), entry, cents: incomeCents(entry), files: filesOf.get(entry.id) ?? [] }] : [];
    });
  const shipments = [...ix.pd.shipments]
    .filter((s) => inMonth(s.shipped_on))
    .sort((a, b) => String(a.shipped_on).localeCompare(String(b.shipped_on)) || a.created_at.localeCompare(b.created_at))
    .flatMap((shipment) => {
      const presale = ix.presaleById.get(shipment.presale_id);
      if (!presale) return [];
      const lines = ix.shipLinesByShipment.get(shipment.id) ?? [];
      let unitCostCents = 0;
      const missingCost: string[] = [];
      const filledLater: string[] = [];
      const byItem = lines.map((l) => {
        const sku = ix.itemById.get(l.item_id)?.sku ?? cx.itemById.get(l.item_id)?.sku ?? "";
        // The cost kept when it was recorded: a past month's pack never changes by itself.
        if (l.unit_cost == null) missingCost.push(sku);
        else unitCostCents += Math.round(mulRound(n(l.qty), n(l.unit_cost) * 100, 4));
        if (l.unit_cost_filled_on) filledLater.push(`${sku} unit cost on ${String(l.unit_cost_filled_on).slice(0, 10)}`);
        return { sku, qty: n(l.qty) };
      });
      if (shipment.deposit_ratio_filled_on) filledLater.push(`share of deposits on ${String(shipment.deposit_ratio_filled_on).slice(0, 10)}`);
      const costs = Object.fromEntries(SHIPPING_KINDS.map((k) => [k, 0])) as Record<ShippingKind, number>;
      const bills = new Set<string>();
      for (const c of cx.costsByRun.get(presale.run_id) ?? [])
        if (c.presale_shipment_id === shipment.id) {
          costs[(c.shipping_kind || "other") as ShippingKind] += costCents(c, 0);
          for (const b of billsByCost.get(c.id) ?? []) bills.add(b);
        }
      // The share kept when it was recorded (deposits so far then ÷ the total pledge value): later payouts never move it.
      const ratio = shipment.deposit_ratio == null ? null : n(shipment.deposit_ratio);
      return [
        {
          presale,
          run: runOf(presale),
          shipment,
          units: byItem.reduce((t, x) => t + x.qty, 0),
          byItem: byItem.sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true })),
          unitCostCents,
          missingCost,
          costs,
          shippingCents: Object.values(costs).reduce((t, c) => t + c, 0),
          depositShareCents: ratio == null ? null : Math.round(n(shipment.pledge_value) * ratio * 100),
          bills: [...bills].sort(),
          filledLater,
        },
      ];
    });
  return {
    income,
    shipments,
    totals: {
      inCents: income.filter((x) => x.cents > 0).reduce((t, x) => t + x.cents, 0),
      outCents: -income.filter((x) => x.cents < 0).reduce((t, x) => t + x.cents, 0),
      pledgeCents: shipments.reduce((t, s) => t + Math.round(mulRound(n(s.shipment.pledge_value), 100, 0)), 0),
      unitCostCents: shipments.reduce((t, s) => t + s.unitCostCents, 0),
      shippingCents: shipments.reduce((t, s) => t + s.shippingCents, 0),
      depositShareCents: shipments.reduce((t, s) => t + (s.depositShareCents ?? 0), 0),
    },
    unknownPledge: [...new Set(shipments.filter((s) => s.depositShareCents == null).map((s) => s.presale.name))],
  };
}

// Import: the backers app's counts-only summary ----------------------------------------------------------------------

/** Keys that are notes, not reservations: sent later by a code (COUPON:*), or units whose colour isn't known (Pxx:no-colour). */
export const skipKey = (key: string) => (/^COUPON:/i.test(key) ? "coupon" : /:no-colour$/i.test(key) ? "no_colour" : null);

/** Old keys the summary may still use for an HHL SKU (the iFixit Edition, before it had one). */
export const KEY_ALIASES: Record<string, string> = { IFX: "GCU1305" };
